/**
 * Despachos en curso (`/despachos`).
 *
 * Portado de `PortalCliente::despachos()` + `api_despacho_en_vivo`: los últimos
 * 10 despachos a crédito con estado `ACTIVO` del cliente y, para los que tienen
 * transacción PTS, una lectura **en vivo** del controlador
 * (`PumpGetTransactionInformation`): cuánto lleva surtido la bomba y cuánto va
 * marcando.
 *
 * Si el controlador no responde, la API devuelve **503** con el motivo: se
 * muestra como aviso, porque que el equipo esté caído no es un error del cliente.
 */

import { Activity, Fuel, RefreshCw, Send } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Spinner } from '@/components/ui/spinner';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateTimeValue, formatNumber } from '@/lib/utils';

import { usePortalDespachoEnVivo, usePortalDespachos } from '../portal.hooks';
import type { DespachoPortal } from '../portal.types';

/**
 * Lectura en vivo de un despacho.
 *
 * Va en su propio componente porque los hooks no pueden llamarse dentro de un
 * `map`: así cada despacho consulta al controlador sólo cuando se pide.
 */
function EnVivo({ despacho }: { despacho: DespachoPortal }) {
  const consulta = usePortalDespachoEnVivo(despacho.lado_id, despacho.transaccion);

  if (consulta.isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-ink-500">
        <Spinner className="size-3.5" />
        Consultando al controlador…
      </div>
    );
  }

  if (consulta.error) {
    const sinConexion = consulta.error instanceof ApiError && consulta.error.status === 503;

    return (
      <Alert variant={sinConexion ? 'warning' : 'error'}>
        {consulta.error instanceof ApiError
          ? consulta.error.message
          : 'No se pudo leer el despacho en vivo.'}
      </Alert>
    );
  }

  const vivo = consulta.data?.despacho;

  if (!vivo) return null;

  return (
    <div className="flex flex-col gap-2 rounded-control border border-ink-100 bg-ink-50/60 p-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5 text-ink-500">
          <Activity className="size-3.5" aria-hidden="true" />
          Estado
          <span className="font-semibold text-ink-900">{vivo.estado ?? '—'}</span>
        </span>
        <span className="text-ink-500">
          Surtido <span className="font-semibold text-ink-900">{formatNumber(vivo.volumen)}</span>
        </span>
        <span className="text-ink-500">
          Importe <span className="font-semibold text-ink-900">{formatCurrency(vivo.monto)}</span>
        </span>
        {vivo.manguera !== null ? (
          <span className="text-ink-500">
            Manguera <span className="font-semibold text-ink-900">{vivo.manguera}</span>
          </span>
        ) : null}
      </div>
      <p className="text-[11px] text-ink-400">
        Lectura del controlador ({vivo.tipo ?? 'jsonPTS'}). Se actualiza al pulsar «Ver en vivo».
      </p>
    </div>
  );
}

export function PortalDespachosPage() {
  useDocumentTitle('Portal · Despachos');
  const consulta = usePortalDespachos();
  const [abierto, setAbierto] = useState<number | null>(null);

  const despachos = consulta.data?.despachos ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TituloPagina
          icono={Send}
          titulo="Despachos en curso"
          descripcion="Tus últimas cargas a crédito que siguen activas. Si el surtidor aún está despachando, puedes ver el avance en vivo."
        />
        <Button
          variant="outline"
          size="sm"
          leftIcon={
            <RefreshCw
              className={cn('size-3.5', consulta.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
          }
          onClick={() => void consulta.refetch()}
        >
          Actualizar
        </Button>
      </div>

      {consulta.error ? (
        <Alert variant="error">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'No se pudieron cargar tus despachos.'}
        </Alert>
      ) : null}

      <SectionCard
        title={`Despachos activos (${despachos.length})`}
        description="Se listan los 10 más recientes con estado activo."
      >
        {consulta.isLoading ? (
          <div className="py-6">
            <Spinner />
          </div>
        ) : despachos.length === 0 ? (
          <EstadoVacio
            icon={Fuel}
            titulo="No tienes despachos a crédito pendientes"
            detalle="Aquí verás el avance de tus cargas mientras el surtidor está despachando."
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {despachos.map((despacho) => {
              const puedeVerEnVivo = despacho.lado_id !== null && despacho.transaccion !== null;

              return (
                <li key={despacho.id} className="rounded-card border border-ink-100 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink-900">
                        <span className="font-mono">{despacho.placa ?? 'sin placa'}</span>
                        <span className="text-ink-400">·</span>
                        <span>{despacho.producto}</span>
                        {despacho.serie ? <Badge variant="outline">{despacho.serie}</Badge> : null}
                        {despacho.tipo === 'GLOBAL' ? <Badge variant="neutral">global</Badge> : null}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-500">
                        {formatDateTimeValue(despacho.fecha)}
                        {despacho.lado ? ` · ${despacho.lado}` : ''}
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs text-ink-500">{formatNumber(despacho.cantidad)} gal</p>
                        <p className="text-sm font-semibold text-ink-900">
                          {formatCurrency(despacho.total)}
                        </p>
                        {despacho.descuento > 0 ? (
                          <p className="text-[11px] text-emerald-600">
                            −{formatCurrency(despacho.descuento)} descuento
                          </p>
                        ) : null}
                      </div>

                      {puedeVerEnVivo ? (
                        <Button
                          variant={abierto === despacho.id ? 'secondary' : 'outline'}
                          size="sm"
                          leftIcon={<Activity className="size-3.5" aria-hidden="true" />}
                          onClick={() => setAbierto(abierto === despacho.id ? null : despacho.id)}
                        >
                          {abierto === despacho.id ? 'Ocultar' : 'Ver en vivo'}
                        </Button>
                      ) : (
                        <span className="text-[11px] text-ink-400">Sin transacción PTS</span>
                      )}
                    </div>
                  </div>

                  {abierto === despacho.id ? (
                    <div className="mt-3">
                      <EnVivo despacho={despacho} />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
